import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, Zap } from 'lucide-react';
import ShiftSlider from './ShiftSlider';
import Select from './Select';
import { ImageFormat, ScaleOption, UploadedImage, getAvailableOutputFormats, initFormatSupport } from '../services/imageService';

interface ConversionSettingsProps {
  selectedFormat: ImageFormat;
  setSelectedFormat: (format: ImageFormat) => void;
  compressionLevel: number;
  setCompressionLevel: (level: number) => void;
  scaleOption: ScaleOption;
  setScaleOption: (scale: ScaleOption) => void;
  predictedSize: string | null;
  uploadedImages: UploadedImage[];
}

const ConversionSettings: React.FC<ConversionSettingsProps> = ({
  selectedFormat,
  setSelectedFormat,
  compressionLevel,
  setCompressionLevel,
  scaleOption,
  setScaleOption,
  predictedSize,
  uploadedImages
}) => {
  const { t } = useTranslation();

  const getFormats = useCallback(
    () => getAvailableOutputFormats(uploadedImages.map(img => img.file)),
    [uploadedImages]
  );

  const [availableFormats, setAvailableFormats] = useState<ImageFormat[]>(getFormats);

  useEffect(() => {
    let cancelled = false;
    initFormatSupport().then(() => {
      if (!cancelled) setAvailableFormats(getFormats());
    });
    return () => { cancelled = true; };
  }, [getFormats]);

  useEffect(() => {
    if (!availableFormats.includes(selectedFormat) && availableFormats.length > 0) {
      setSelectedFormat(availableFormats[0]);
    }
  }, [availableFormats, selectedFormat, setSelectedFormat]);

  const formatDisplayNames: Partial<Record<ImageFormat, string>> = {
    jpeg: 'JPEG', png: 'PNG', webp: 'WebP', avif: 'AVIF',
    gif: 'GIF', bmp: 'BMP', ico: 'ICO',
  };

  const formatTips: Partial<Record<ImageFormat, string>> = {
    jpeg: 'Best for photos. Great quality at small file sizes.',
    png:  'Lossless with transparency. Best for screenshots and graphics.',
    webp: 'Excellent quality-to-size ratio. Supported in all modern browsers.',
    avif: 'Smallest file sizes of any format. Best for web delivery.',
    gif:  'Supports animation. Limited to 256 colors per frame.',
    bmp:  'Uncompressed bitmap. Very large — use only when raw data is needed.',
    ico:  'Icon format for favicons and apps.',
  };

  const formatOptions = availableFormats.map(format => ({
    value: format,
    label: formatDisplayNames[format] ?? format.toUpperCase(),
  }));

  const scaleOptions = [
    { value: '0.5', label: '50%' },
    { value: '0.75', label: '75%' },
    { value: '1', label: '100%' },
    { value: '1.5', label: '150%' },
    { value: '2', label: '200%' },
  ];

  return (
    <section aria-labelledby="conversion-settings">
      <div className="card">
        <div className="card-header">
          <h2 id="conversion-settings" className="text-xl flex items-center m-0">
            <Settings className="mr-2 text-primary-500" size={20} aria-hidden="true" />
            {t('settings.title')}
          </h2>
        </div>
        <div className="card-body flex flex-col gap-6">

          <Select
            id="format-select"
            label={t('settings.format')}
            value={selectedFormat}
            options={formatOptions}
            onChange={(value) => setSelectedFormat(value as ImageFormat)}
          />

          <div>
            <label id="compression-label" className="label block mb-3">
              {t('settings.compression.title')}: {compressionLevel}%
            </label>
            <ShiftSlider
              id="compression-slider"
              min={0}
              max={100}
              value={compressionLevel}
              onChange={setCompressionLevel}
              ariaLabelledBy="compression-label"
            />
            <p className="text-xs text-gray-700 mt-3 mb-0 flex items-start gap-1">
              <Zap size={12} className="mt-0.5 shrink-0 text-primary-600" aria-hidden="true" />
              {formatTips[selectedFormat] ?? ''}
            </p>
          </div>

          <Select
            id="scale-select"
            label={t('settings.scale')}
            value={scaleOption.toString()}
            options={scaleOptions}
            onChange={(value) => setScaleOption(parseFloat(value) as ScaleOption)}
          />

          {predictedSize && (
            <div className="p-3 bg-gray-100 rounded-[1rem] border border-gray-200">
              <p className="text-xs text-gray-600 mb-1">{t('settings.estimatedSize')}</p>
              <p className="font-semibold text-gray-900 mb-0 flex items-center gap-1">
                <Zap size={14} className="text-primary-600 shrink-0" aria-hidden="true" />
                {predictedSize}
              </p>
            </div>
          )}

        </div>
      </div>
    </section>
  );
};

export default ConversionSettings;
